<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate />

    <template v-if="!loading && arrangements.length > 0">
      <v-data-table
        :headers="tableHeaders"
        :items="sortedArrangements"
        item-key="id"
        class="elevation-0"
        hide-default-footer
        :items-per-page="-1"
        dense
      >
        <template v-slot:item.name="{ item }">
          <span class="font-weight-medium">
            {{ item.name || $t('conglomerates.no_name') }}
          </span>
        </template>

        <template v-slot:item.arrangementType="{ item }">
          {{ getTypeLabel(item.arrangementType) }}
        </template>

        <template v-slot:item.totalLicenses="{ item }">
          {{ item.totalLicenses }} {{ $t('conglomerates.licenses') }}
        </template>

        <template v-slot:item.dates="{ item }">
          {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            v-if="isActive(item)"
            x-small
            color="success"
            text-color="white"
          >
            <v-icon left x-small>mdi-check-circle</v-icon>
            {{ $t('conglomerates.status_created') }}
          </v-chip>
          <v-chip
            v-else
            x-small
            color="grey"
            text-color="white"
          >
            {{ $t('conglomerates.status_inactive') }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            small
            icon
            :aria-label="$t('conglomerates.edit_agreement')"
            @click="$emit('edit', item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </template>

    <v-sheet
      v-if="!loading && arrangements.length === 0"
      class="py-8 px-4 text-center"
      rounded
      outlined
    >
      <v-icon color="grey lighten-1" size="56" class="mb-3">mdi-file-document-outline</v-icon>
      <div class="subtitle-1 font-weight-medium mb-1">{{ $t('conglomerates.no_agreements') }}</div>
      <div class="body-2 grey--text text--darken-1 mb-4">
        {{ $t('conglomerates.no_agreements_description') }}
      </div>
      <v-btn v-if="!readonly" color="deep-purple" outlined @click="$emit('create')">
        <v-icon left>mdi-plus</v-icon>
        {{ $t('conglomerates.create_agreement') }}
      </v-btn>
    </v-sheet>
  </div>
</template>

<script>
export default {
  name: 'ConglomerateArrangementsCard',
  props: {
    arrangements: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  computed: {
    sortedArrangements() {
      return [...this.arrangements].sort((a, b) => {
        const aActive = this.isActive(a);
        const bActive = this.isActive(b);
        if (aActive !== bActive) return aActive ? -1 : 1;
        return new Date(b.endDate || b.end_date) - new Date(a.endDate || a.end_date);
      });
    },
    tableHeaders() {
      const headers = [
        { text: this.$t('conglomerates.agreement_name'), value: 'name', sortable: true },
        { text: this.$t('conglomerates.agreement_type'), value: 'arrangementType', sortable: true },
        { text: this.$t('conglomerates.agreement_total_licenses'), value: 'totalLicenses', sortable: true },
        { text: this.$t('conglomerates.agreement_dates'), value: 'dates', sortable: false },
        { text: this.$t('conglomerates.status'), value: 'status', sortable: false },
      ];
      if (!this.readonly) {
        headers.push({ text: this.$t('conglomerates.actions'), value: 'actions', sortable: false });
      }
      return headers;
    },
  },
  methods: {
    parseDate(dateString) {
      if (!dateString) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(dateString);
    },
    isActive(arrangement) {
      if (!arrangement) return false;
      if (arrangement.status) {
        return arrangement.status === 'C';
      }
      const endDate = this.parseDate(arrangement.endDate || arrangement.end_date);
      if (!endDate || Number.isNaN(endDate.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return endDate >= today;
    },
    getTypeLabel(type) {
      const labels = {
        Demo: this.$t('conglomerates.type_demo'),
        Trial: this.$t('conglomerates.type_trial'),
        Client: this.$t('conglomerates.type_client'),
      };
      return labels[type] || type;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = this.parseDate(dateString);
      if (!date || Number.isNaN(date.getTime())) return '-';
      return date.toLocaleDateString(this.$i18n?.locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    },
  },
};
</script>
